import React, { useEffect, useState, useRef } from 'react'
import { userActions } from 'redux/user/userSlice'
import { Helmet } from 'react-helmet-async'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { AppState } from 'redux/config/store'
import { Locations } from 'shared/Router/Locations'
import UrlParser from 'shared/UrlParser'
import styled from 'styled-components'
import { LoginForm } from './components/LoginForm'
import { applicationActions } from 'redux/application/applicationSlice'
import LocalStorage from 'shared/LocalStorage'
import FleetLogoFullColor from 'core-system/FleetLogos/FleetLogoFullColor'
import LoginSignUpGraphicV2, {
  ImageLabel,
} from './components/LoginSignUpGraphicV2'
import ForgotPasswordForm from './ForgotPasswordForm'
import { isEmployerGettingStartedOnboarding } from 'features/Onboarding/helperFunctions'
import { getPlatformType } from 'shared/Router/Utils'
import { getClientUrl } from 'redux/config/services/Constants'
import { extractCompanyIdFromState } from 'features/SSO/utils'
import { getTokenExpirationFromCookies } from 'shared/JwtCookieUtils'

const Container = styled.div`
  display: flex;
  height: 100%;
`

const LogoContainer = styled.div`
  margin-bottom: 3rem;
`

const LeftContainer = styled.div<{ loginType: any }>`
  width: 50%;
  background-color: ${(props) => props.theme.palette.white};
  padding: 3rem 4rem;
  display: flex;
  position: relative;
  flex-direction: column;
  overflow: scroll;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: ${(props) => props.theme.breakpoints[2]}) {
    width: 100%;
  }
`

const FormContainer = styled.div`
  a {
    width: 100%;
    height: 100%;
  }
`

const LoginView = React.memo(() => {
  const dispatch = useDispatch()
  const location = useLocation()

  const profile = useSelector((state: AppState) => state.employer.profile)
  const onboarding = useSelector((state: AppState) => state.onboarding)
  const isEmployeePlatform = useSelector(
    (state: AppState) => state.application.isEmployeePlatform
  )

  const qp = UrlParser.getQueryParams(location.search)
  const userErr = useSelector((state: AppState) => state.user.error)
  const navigate = useNavigate()

  const [loginType, setLoginType] = useState(getPlatformType() || 'employer')
  const [pageState, setPageState] = useState('login')

  // Track if we've already dispatched social login for current parameters
  const socialLoginDispatchedRef = useRef<string | null>(null)
  // Track if we've already dispatched SSO login for current parameters
  const ssoLoginDispatchedRef = useRef<string | null>(null)
  // Track if we've already handled IdP-initiated SSO
  const idpInitiatedSsoHandledRef = useRef<boolean>(false)

  // If there is a query forgot password, set the page state to forgot password
  useEffect(() => {
    if (qp.forgot_password) {
      setPageState('forgotPassword')
    }
  }, [qp.forgot_password])

  const toggleLoginType = (currLoginType) => {
    setLoginType(currLoginType === 'employer' ? 'employee' : 'employer')
    applicationActions.toggleEmployeePlatform()
    LocalStorage.setItem(
      'platformType',
      currLoginType === 'employer' ? 'employee' : 'employer'
    )
  }

  console.log('ENV VARS', process.env)
  console.log('REACT APP API SERVER', process.env.REACT_APP_API_SERVER)

  // redirect on successful login
  useEffect(() => {
    if (userErr?.status === false) {
      if (loginType === 'employer') {
        if (!profile) {
          return
        }

        if (onboarding.onboardingFetchingStatus === 'fetching') {
          return
        }

        if (
          isEmployerGettingStartedOnboarding(onboarding) ||
          qp.new_signup === 'true' ||
          qp.from_signup === 'true'
        ) {
          navigate(Locations.GettingStarted)
        } else {
          navigate(Locations.Dashboard)
        }
      } else {
        navigate(Locations.EmployeeDashboard)
      }
    }
  }, [userErr, navigate, loginType, qp, profile, onboarding])

  // SSO login success redirect back to app (Okta / Azure enterprise SSO)
  useEffect(() => {
    if (qp.code && qp.state) {
      // Extract company ID from state parameter (format: {provider}_{companyId}_{timestamp})
      const companyId = extractCompanyIdFromState(qp.state)

      // Only proceed if we can extract a valid company ID from the state
      if (companyId) {
        // Create a unique key for the current SSO login parameters
        const currentKey = `${qp.code}_${qp.state}_${companyId}_${loginType}`

        // Only dispatch if we haven't already dispatched for these parameters
        if (ssoLoginDispatchedRef.current !== currentKey) {
          dispatch(
            userActions.ssoLogin({
              code: qp.code,
              companyId: companyId,
              state: qp.state,
              platformType: loginType,
              redirectUri: `${getClientUrl()}/login?state=${qp.state}&code=${qp.code}`,
            })
          )
          // Mark that we've dispatched for these parameters
          ssoLoginDispatchedRef.current = currentKey
        }
      }
    }
  }, [qp, dispatch, loginType])

  // Social Auth login success redirect back to app (Google/Microsoft)
  useEffect(() => {
    // Skip social login when state is an enterprise SSO state (okta_/azure_)
    if (qp.code && qp.state && !extractCompanyIdFromState(qp.state)) {
      // Create a unique key for the current social login parameters
      const currentKey = `${qp.code}_${qp.state}_${loginType}`

      // Only dispatch if we haven't already dispatched for these parameters
      if (socialLoginDispatchedRef.current !== currentKey) {
        const isMsft = !qp.scope ? true : false
        dispatch(
          userActions.socialLogin({
            code: qp.code,
            isMsft,
            state: qp.state,
            platformType: loginType,
          })
        )
        // Mark that we've dispatched for these parameters
        socialLoginDispatchedRef.current = currentKey
      }
    }
  }, [qp, dispatch, loginType])

  // IdP-initiated SSO login (backend sets JWT cookies and redirects here)
  useEffect(() => {
    if (qp.idp_initiated === 'true' && !idpInitiatedSsoHandledRef.current) {
      // Mark as handled to prevent duplicate processing
      idpInitiatedSsoHandledRef.current = true

      // IdP-initiated SSO is always for employee platform
      // Set loginType state to employee
      setLoginType('employee')
      // Toggle application platform to employee (if currently employer, this will switch it)
      if (!isEmployeePlatform) {
        dispatch(applicationActions.toggleEmployeePlatform())
      }
      // Set platform type in localStorage
      LocalStorage.setItem('platformType', 'employee')

      // Check if JWT token exists in cookies (may be HttpOnly, which is fine)
      const tokenExp = getTokenExpirationFromCookies()

      // Even if we can't read the cookie (HttpOnly), proceed with login
      // The cookie will be sent automatically with API requests
      dispatch(
        userActions.idpInitiatedSsoSuccess({
          platformType: 'employee',
          tokenExp: tokenExp, // Pass tokenExp if found
        })
      )

      // Fetch user profile (cookies will be sent automatically with the request)
      // This will work even if the cookie is HttpOnly
      dispatch(userActions.getUser())
    }
  }, [qp.idp_initiated, dispatch, isEmployeePlatform])

  return (
    <>
      <Helmet>
        <title>Login | Fleet</title>
      </Helmet>
      <Container>
        <LeftContainer loginType={loginType}>
          <LogoContainer>
            <FleetLogoFullColor height='40' width='170' />
          </LogoContainer>
          <FormContainer>
            {pageState === 'login' ? (
              <LoginForm
                userErr={userErr}
                loginType={loginType}
                toggleLoginType={toggleLoginType}
                setPageState={setPageState}
                qp={qp}
              />
            ) : (
              <ForgotPasswordForm setPageState={setPageState} />
            )}
          </FormContainer>
        </LeftContainer>
        <LoginSignUpGraphicV2
          platformType={loginType}
          imageLabel={ImageLabel.LOGIN}
        />
      </Container>
    </>
  )
})

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  LoginView.displayName = 'Login View'
}

export default LoginView
